import { useCallback, useEffect, useState } from 'react';
import { IconLifebuoy } from '@tabler/icons-react';
import { unwrapData } from '@wapharma/shared';
import { Button, WP_ICON_NAV } from '@wapharma/design-system';
import { buildReportIssueContext } from '@wapharma/shared';
import { apiPost, apiPostForm } from '../api';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { ReportIssuePopoverWizard, type ReportIssueSubmitPayload } from './ReportIssuePopoverWizard';
import styles from './ReportIssueWidget.module.css';

type Props = {
  variant?: 'button' | 'link' | 'floating';
  className?: string;
  defaultOpened?: boolean;
  onClosed?: () => void;
  hideTrigger?: boolean;
  traceId?: string | null;
};

export function ReportIssueWidget({
  variant = 'button',
  className,
  defaultOpened = false,
  onClosed,
  hideTrigger = false,
  traceId,
}: Props) {
  const [opened, setOpened] = useState(defaultOpened);
  const { user } = useAuth();
  const { showToast } = useToast();

  useEffect(() => {
    setOpened(defaultOpened);
  }, [defaultOpened]);

  const submit = useCallback(
    async (payload: ReportIssueSubmitPayload) => {
      const token = getToken();
      const body = {
        type: payload.type,
        subject: payload.subject,
        description: payload.description,
        email: payload.email,
        context: buildReportIssueContext({ surface: 'admin' }),
      };

      if (payload.files?.length) {
        const fd = new FormData();
        fd.append('data', JSON.stringify(body));
        for (const file of payload.files) {
          fd.append('files', file, file.name);
        }
        const res = await apiPostForm<{ data: { reference: string } }>(
          '/support/tickets',
          fd,
          token ?? undefined,
        );
        const ticket = unwrapData<{ reference: string }>(res);
        showToast(`Ticket ${ticket.reference} enregistré.`, 'success');
        return;
      }

      const res = await apiPost<{ data: { reference: string } }>(
        '/support/tickets',
        body,
        token ?? undefined,
      );
      const ticket = unwrapData<{ reference: string }>(res);
      showToast(`Ticket ${ticket.reference} enregistré.`, 'success');
    },
    [showToast],
  );

  const close = () => {
    setOpened(false);
    onClosed?.();
  };

  const modal = (
    <ReportIssuePopoverWizard
      opened={opened}
      onClose={close}
      onSubmit={submit}
      defaultEmail={user?.email}
      requireEmail={!user}
      traceId={traceId}
    />
  );

  if (hideTrigger) {
    return modal;
  }

  const trigger =
    variant === 'floating' ? (
      <button
        type="button"
        className={[styles.floating, className].filter(Boolean).join(' ')}
        onClick={() => setOpened(true)}
        aria-label="Signaler un problème"
      >
        <IconLifebuoy {...WP_ICON_NAV} aria-hidden />
      </button>
    ) : variant === 'link' ? (
      <Button type="button" variant="ghost" className={className} onClick={() => setOpened(true)}>
        Signaler un problème
      </Button>
    ) : (
      <Button type="button" variant="soft" className={className} onClick={() => setOpened(true)}>
        Signaler un problème
      </Button>
    );

  return (
    <>
      {trigger}
      {modal}
    </>
  );
}
